
import React, { useState, useEffect, useContext } from "react";
import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import KeyPerformanceIndicators from "layouts/dashboard/components/KeyPerformanceIndicator";
import ConnectOwnProfile from "layouts/dashboard/components/ConnectOwnProfile";

import { AuthContext } from "context";
import AuthService from "services/auth-service";
import WorkspaceService from "services/workspace-service"; // [NEW]
import { usePermissions } from "context"; // [NEW]
import UpgradePopup from "layouts/components/Upgrade"; // [NEW]
import LiveDemoRestriction from "layouts/components/Upgrade/liveDemo"; // [NEW]
import DashboardIntroStep from "layouts/tutorial/steps/DashboardIntroStep";
import LiveDemoIntroStep from "layouts/tutorial/steps/LiveDemoIntroStep"; // [NEW]

function Dashboard() {
  const { isAuthenticated } = useContext(AuthContext);
  const [openConnectProfile, setOpenConnectProfile] = useState(false);

  // Tutorial State
  const [showTutorial, setShowTutorial] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  // [NEW] Live Demo Intro Modal — show once per session
  const isLiveDemo = sessionStorage.getItem('demo_role_cache') === "demo_viewer";
  const [showDemoIntro, setShowDemoIntro] = useState(
    () => isLiveDemo && sessionStorage.getItem('demo_intro_seen') !== 'true'
  );
  const handleDemoIntroComplete = () => {
    sessionStorage.setItem('demo_intro_seen', 'true');
    setShowDemoIntro(false);
  };

  // [NEW] Usage Enforcement State
  const [usageRaw, setUsageRaw] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [showDemoPopup, setShowDemoPopup] = useState(false); // [NEW] Demo Popup State

  // [NEW] RBAC State
  const [showRBAC, setShowRBAC] = useState(false);
  const { hasPermission, requiredRoles } = usePermissions();

  const handleAddProfileClick = () => {
    // [NEW] RBAC Check
    if (!hasPermission("stash:manage")) {
      setShowRBAC(true);
      return;
    }

    // [NEW] Live Demo Restriction
    const isDemo = sessionStorage.getItem('demo_role_cache') === "demo_viewer";
    if (isDemo) {
      setShowDemoPopup(true);
      return;
    }

    // [NEW] Check Usage Limit
    if (usageRaw) {
      const { profilesUsed, profilesLimit } = usageRaw;
      if (Number(profilesUsed) >= Number(profilesLimit)) {
        setShowUpgrade(true);
        return;
      }
    }
    setOpenConnectProfile(true);
  };

  const handleCloseConnectProfile = () => {
    setOpenConnectProfile(false);
  };

  // Check Tutorial Status on Mount
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkTutorial = async () => {
      try {
        const resp = await AuthService.getProfile();
        const userData = resp.data.attributes;
        setCurrentUser(userData);

        const completedSteps = userData.completed_tutorials || [];
        if (!completedSteps.includes("dashboard_intro")) {
          setShowTutorial(true);
        }
      } catch (error) {
        console.error("Failed to check tutorial status:", error);
      }
    };
    checkTutorial();
    fetchUsageData(); // [NEW] Fetch usage on mount
  }, [isAuthenticated]);

  // [NEW] Fetch Usage Logic
  const fetchUsageData = async () => {
    try {
      const entitlements = await WorkspaceService.fetchWorkspaceEntitlements();
      if (!entitlements) return;

      setUsageRaw({
        profilesUsed: entitlements.usage?.profiles || 0,
        profilesLimit: entitlements.limits?.profiles || 0,
        currentPlan: entitlements.plan || "Basic"
      });
    } catch (error) {
      console.error("Error fetching usage details:", error);
    }
  };

  const handleCompleteTutorial = async () => {
    try {
      const currentCompleted = currentUser?.completed_tutorials || [];
      if (!currentCompleted.includes("dashboard_intro")) {
        const updatedTutorials = [...currentCompleted, "dashboard_intro"];
        await AuthService.updateProfile({
          data: { attributes: { completed_tutorials: updatedTutorials } }
        });
      }
      setShowTutorial(false);
    } catch (error) {
      console.error("Failed to complete tutorial:", error);
      setShowTutorial(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox pt={6} pb={6}>
        {/* ✅ Connect Own Profile modal — always available here */}
        <ConnectOwnProfile
          open={openConnectProfile}
          onClose={handleCloseConnectProfile}
        />

        {/* KPIs */}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <KeyPerformanceIndicators onAddProfileClick={handleAddProfileClick} />
          </Grid>
        </Grid>

      </MDBox>

      <Footer />

      {/* Dashboard Tutorial Popup */}
      {isLiveDemo ? (
        <LiveDemoIntroStep
          open={showDemoIntro}
          onComplete={handleDemoIntroComplete}
          onSkip={handleDemoIntroComplete}
        />
      ) : (
        <DashboardIntroStep
          open={showTutorial}
          onComplete={handleCompleteTutorial}
          onSkip={() => setShowTutorial(false)}
        />
      )}

      {/* [NEW] Upgrade Popup */}
      <UpgradePopup
        open={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        type="usage"
        featureName="Tracked Profiles"
        planRequired="Higher Plan"
        currentPlan={usageRaw?.currentPlan}
        usageLimit={usageRaw?.profilesLimit}
      />

      {/* [NEW] Live Demo Restriction Popup */}
      <LiveDemoRestriction
        open={showDemoPopup}
        onClose={() => setShowDemoPopup(false)}
        featureName="Connecting Your Profile"
      />

      {/* [NEW] RBAC Restriction Popup */}
      <UpgradePopup
        open={showRBAC}
        onClose={() => setShowRBAC(false)}
        type="rbac"
        featureName="Connecting Profiles"
        roleRequired={requiredRoles["stash:manage"] || "Manager"}
      />
    </DashboardLayout>
  );
}

export default Dashboard;
